// src/theme/palette.ts
import { PaletteOptions } from '@mui/material/styles';

// Cool neutral scale (Tailwind slate) — kept for DARK mode only. Light mode now
// uses the warm scale below (v3 warm-neutral migration is light-only; dark stays
// on the proven cool slate to avoid an unspecified dark redesign).
const grey = {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
};

// Warm neutral scale (v3 "warm-neutral" direction) — cream→brown, driving the
// light-mode canvas, borders and micro-tones. Mirrors roommatik.console.ui's v3
// grey ramp. Only the light palette consumes this; dark keeps `grey` (slate).
const warmGrey = {
    50: '#F7F5F1',
    100: '#EEEAE3',
    200: '#DFDBD1',
    300: '#CDC7BB',
    400: '#A19A8D',
    500: '#77726A',
    600: '#57534C',
    700: '#3C3934',
    800: '#2C2926',
    900: '#282624',
};

const blue = {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    300: '#93C5FD',
    400: '#60A5FA',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8',
    800: '#1E40AF',
    900: '#1E3A8A',
};

// Brand teal — primary identity color, shared across modes (brand tokens:
// Signal Teal / Brand Teal / Deep Teal).
const brandPrimary = {
    main: '#0BA5B7',
    light: '#55C3D2',
    dark: '#0E7C8A',
    contrastText: '#FFFFFF',
};

// Warm brand accent (v2 "accentWarm"). Exposed through palette.warning so it
// is consumed via theme tokens, never hardcoded in components. It is the ONLY
// hot spot of the UI: one CTA per screen + "pending" status tints. Never a
// surface, chart-series or active-menu colour.
export const accentWarm = {
    main: '#F2A93B',
    light: '#F7C577',
    dark: '#B5710E',
};

// Teal used as TEXT (eyebrow labels, inline links/actions). Darker than
// Signal Teal so it keeps AA contrast on white; brightened on dark surfaces.
export const brandTextTeal = {
    light: '#147A87',
    dark: '#55C3D2',
};

// ── Colour rule (single source of truth) ───────────────────────────────────
//   primary (Deep Teal)     → ONLY the side rail + logo. Never in data content.
//   dataAccent (light teal) → data zone: .main fills (bars/series/tiles),
//                             .dark emphasis (text/borders/highlighted value).
//   success / error    → ONLY semantic states (completed / abandoned).
//   warning (orange)   → the single warm CTA per screen (variant warmCta).
//   grey               → structure, borders, text.
// Data accent — the calm teal for the data zone (bars, series, tiles, emphasis).
// Two-tone by design: `main` (light) for FILLS; `dark` for EMPHASIS — text,
// borders, highlighted values. Because `main` is light, `contrastText` is dark
// ink (readable label on a light fill).
// v3.11 (light mode): retoned to the nav-rail family (`#256B7E`) so charts read
// as the same teal as the rail — `dark` = the rail tone, `main`/`light` are
// lighter tints of that same blue-teal hue. Dark mode keeps its brighter data
// teal (the v3 migration is light-only; a dark rail-tone fill would be too low
// contrast on the dark canvas).
export const dataAccent = {
    light: { main: '#8FBAC4', light: '#C2DAE1', dark: '#256B7E', contrastText: '#06333A' },
    dark: { main: '#8CCACB', light: '#C9E7E7', dark: '#A7DADB', contrastText: '#06333A' },
};

export const lightPalette: PaletteOptions = {
    mode: 'light',
    primary: brandPrimary,
    // Warm grey secondary — drives neutral text, secondary buttons and
    // warm-grey borders. Matches the 3c Hybrid spec already.
    secondary: {
        main: '#77726A',
        light: '#A19A8D',
        dark: '#57534C',
        contrastText: '#FFFFFF',
    },
    // 3c Hybrid canvas (hybrid-3c.md §2 "Surfaces"): a warm-white canvas, a
    // white app bar/paper, and a LIGHT NEUTRAL rail — not a brand-colour
    // block. Teal is spent on the active rail entry alone (rule 1).
    background: {
        default: '#F9F8F6',
        paper: '#FFFFFF',
        appbar: '#FFFFFF',
        menu: '#F4F2EE',
    },
    text: {
        primary: '#282624',
        secondary: '#63605A',
    },
    error: {
        main: '#FB7185',
        light: '#FDA4AF',
        dark: '#9F1239',
        contrastText: '#FFFFFF',
    },
    // Spec warning (hybrid-3c.md §2 "Status") — replaces the old `accentWarm`
    // amber. `accentWarm` stays exported (still consumed as a raw constant by
    // a handful of screens) but no longer backs the theme's `warning` role.
    warning: {
        main: '#FB923C',
        light: '#FDBA74',
        dark: '#9A3412',
        contrastText: '#334155',
    },
    info: {
        main: '#3B82F6',
        light: '#93C5FD',
        dark: '#1D4ED8',
        contrastText: '#FFFFFF',
    },
    dataAccent: dataAccent.light,
    success: {
        main: '#16A34A',
        light: '#4ADE80',
        dark: '#15803D',
        contrastText: '#FFFFFF',
    },
    // borderCard = borderRow = divider, one hairline tone (hybrid-3c.md §2).
    divider: '#E2DFD9',
    grey: warmGrey,
    blue,
    white: '#FFFFFF',
};

/** The 3c Hybrid destructive-fill red (hybrid-3c.md §2) — a separate
 *  constant, never `palette.error` (this theme's `error.main` is the salmon
 *  above, reserved for inline field validation). Never body text; a fill or
 *  border only. Destructive text uses `error.dark`. */
export const DANGER_COLOR = '#EF4444';

export const darkPalette: PaletteOptions = {
    mode: 'dark',
    // Slightly brighter teal so the brand reads on dark surfaces; dark text on it.
    primary: {
        main: '#22B8D6',
        light: '#5BD0E4',
        dark: '#0BA5B7',
        contrastText: '#06222B',
    },
    secondary: {
        main: '#94A3B8',
        light: '#CBD5E1',
        dark: '#64748B',
        contrastText: '#0F172A',
    },
    background: {
        default: '#0F172A',
        paper: '#1E293B',
        appbar: '#0F172A',
        // Same solid Deep Teal rail as light mode — the brand colour separates
        // the rail from the canvas in both modes (white/translucent-white items).
        menu: '#0E7C8A',
    },
    text: {
        primary: '#F1F5F9',
        secondary: '#94A3B8',
    },
    error: {
        main: '#FB7185',
        light: '#FDA4AF',
        dark: '#9F1239',
        contrastText: '#1A0307',
    },
    warning: {
        ...accentWarm,
        contrastText: '#1A0A02',
    },
    info: {
        main: '#60A5FA',
        light: '#93C5FD',
        dark: '#1E40AF',
        contrastText: '#04122E',
    },
    dataAccent: dataAccent.dark,
    success: {
        main: '#4ADE80',
        light: '#86EFAC',
        dark: '#15803D',
        contrastText: '#03190B',
    },
    divider: 'rgba(148, 163, 184, 0.28)',
    grey,
    blue,
    white: '#FFFFFF',
};
